// apps/client/src/components/AIChat/VisualizationRenderer.tsx
// @ts-nocheck
// Note: Recharts has type incompatibilities with React 18/19. Suppressing type checks for this file.

import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { Visualization } from './types';

interface VisualizationRendererProps {
  visualization: Visualization;
}

// Default chart colors (Valutten brand colors)
const DEFAULT_COLORS = [
  '#2563eb', // blue-600
  '#10b981', // green-500
  '#f59e0b', // amber-500
  '#ef4444', // red-500
  '#8b5cf6', // violet-500
  '#ec4899', // pink-500
];

/**
 * Renders visualizations (charts, tables, metrics) from AI responses
 */
export function VisualizationRenderer({ visualization }: VisualizationRendererProps) {
  const { type, title, data, config = {} } = visualization;

  if (!data) {
    return null;
  }

  return (
    <div className="mt-3 bg-gray-800/50 backdrop-blur-sm rounded-lg p-3 border border-gray-600">
      {title && (
        <h4 className="text-sm font-semibold text-gray-100 mb-2">{title}</h4>
      )}

      <div className="w-full">
        {type === 'line' && <LineChartRenderer data={data} config={config} />}
        {type === 'bar' && <BarChartRenderer data={data} config={config} />}
        {type === 'pie' && <PieChartRenderer data={data} config={config} />}
        {type === 'table' && <TableRenderer data={data} config={config} />}
        {type === 'metric_card' && <MetricCardRenderer data={data} config={config} />}
      </div>
    </div>
  );
}

/**
 * Line chart for trends over time
 * Note: Data values are already in dollars (backend converts cents→dollars)
 */
function LineChartRenderer({ data, config }: { data: any[]; config: any }) {
  const xKey = config.xAxisKey || 'x' || 'name';
  const yKey = config.yAxisKey || 'y' || 'value';
  const colors = config.colors || DEFAULT_COLORS;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <LineChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis
          dataKey={xKey}
          tick={{ fontSize: 11, fill: '#9ca3af' }}
          stroke="#4b5563"
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#9ca3af' }}
          stroke="#4b5563"
          tickFormatter={(value) =>
            config.format?.currency ? `$${value.toLocaleString()}` : value
          }
        />
        <Tooltip
          formatter={(value: any) =>
            config.format?.currency
              ? `$${value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : value
          }
          contentStyle={{ fontSize: 12, backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '6px' }}
          labelStyle={{ color: '#e5e7eb' }}
        />
        <Line
          type="monotone"
          dataKey={yKey}
          stroke={colors[0]}
          strokeWidth={2}
          dot={{ r: 3 }}
          activeDot={{ r: 5 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Bar chart for comparisons
 * Note: Data values are already in dollars (backend converts cents→dollars)
 */
function BarChartRenderer({ data, config }: { data: any[]; config: any }) {
  const xKey = config.xAxisKey || 'label' || 'name';
  const yKey = config.yAxisKey || 'value';
  const colors = config.colors || DEFAULT_COLORS;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis
          dataKey={xKey}
          tick={{ fontSize: 11, fill: '#9ca3af' }}
          stroke="#4b5563"
        />
        <YAxis
          tick={{ fontSize: 11, fill: '#9ca3af' }}
          stroke="#4b5563"
          tickFormatter={(value) =>
            config.format?.currency ? `$${value.toLocaleString()}` : value
          }
        />
        <Tooltip
          formatter={(value: any) =>
            config.format?.currency
              ? `$${value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : value
          }
          contentStyle={{ fontSize: 12, backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '6px' }}
          labelStyle={{ color: '#e5e7eb' }}
        />
        <Bar dataKey={yKey} fill={colors[0]} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Pie chart for distributions
 */
function PieChartRenderer({ data, config }: { data: any[]; config: any }) {
  const dataKey = config.dataKey || 'value';
  const nameKey = config.xAxisKey || 'name';
  const colors = config.colors || DEFAULT_COLORS;

  return (
    <ResponsiveContainer width="100%" height={200}>
      <PieChart>
        <Pie
          data={data}
          dataKey={dataKey}
          nameKey={nameKey}
          cx="50%"
          cy="50%"
          outerRadius={60}
          label={(entry) => `${entry[nameKey]}: ${entry[dataKey]}%`}
          labelLine={{ stroke: '#6b7280' }}
        >
          {data.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
          ))}
        </Pie>
        <Tooltip
          contentStyle={{ fontSize: 12, backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '6px' }}
          labelStyle={{ color: '#e5e7eb' }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

/**
 * Table for detailed data
 */
function TableRenderer({ data, config }: { data: any[]; config: any }) {
  if (!Array.isArray(data) || data.length === 0) {
    return <p className="text-sm text-gray-500">No data available</p>;
  }

  // Auto-detect columns if not specified
  const columns = config.columns || Object.keys(data[0]).map((key) => ({
    key,
    label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
    type: 'text',
  }));

  const formatValue = (value: any, column: any) => {
    if (value === null || value === undefined) return '-';

    if (column.format) {
      return column.format(value);
    }

    if (column.type === 'currency') {
      return `$${(value / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }

    if (column.type === 'number') {
      return value.toLocaleString('en-AU');
    }

    if (column.type === 'date') {
      return new Date(value).toLocaleDateString('en-AU');
    }

    return value;
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-600 text-xs">
        <thead className="bg-gray-700/50">
          <tr>
            {columns.map((col: any) => (
              <th
                key={col.key}
                className="px-3 py-2 text-left font-medium text-gray-300 uppercase tracking-wider"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-600">
          {data.map((row, idx) => (
            <tr key={idx} className="hover:bg-gray-700/30">
              {columns.map((col: any) => (
                <td key={col.key} className="px-3 py-2 whitespace-nowrap text-gray-200">
                  {formatValue(row[col.key], col)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Metric card for single key values
 */
function MetricCardRenderer({ data, config }: { data: any; config: any }) {
  const { value, label, change, changeLabel } = data;

  const formatValue = (val: any) => {
    // Handle undefined, null, or NaN
    if (val === undefined || val === null || (typeof val === 'number' && isNaN(val))) {
      return '$0.00';
    }

    // If value is already a string, try to parse it
    const numVal = typeof val === 'string' ? parseFloat(val.replace(/[,$]/g, '')) : val;

    if (isNaN(numVal)) {
      return typeof val === 'string' ? val : '$0.00';
    }

    if (config.format?.currency) {
      // Check if value is in cents (large number) or dollars (smaller number with decimals)
      const isCents = numVal > 10000 && Number.isInteger(numVal);
      const dollars = isCents ? numVal / 100 : numVal;
      return `$${dollars.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (config.format?.percentage) {
      return `${numVal}%`;
    }
    return numVal.toLocaleString('en-AU');
  };

  return (
    <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg p-4 border border-gray-600">
      <p className="text-xs text-gray-400 mb-1">{label}</p>
      <p className="text-2xl font-bold text-white">{formatValue(value)}</p>
      {change !== undefined && (
        <p className={`text-xs mt-1 ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {change >= 0 ? '↑' : '↓'} {Math.abs(change)}% {changeLabel || 'vs last period'}
        </p>
      )}
    </div>
  );
}
